import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  ButtonGroup,
  Checkbox,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  useTheme,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaSearch, FaSpider } from "react-icons/fa";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import SupplierRegistry from "./components/SupplierRegistry";
import AlertSuppliers from "./AlertSuppliers";

const API = process.env.REACT_APP_API_URL;

// Retailers that have a crawler available on the IONOS crawler service. The
// upstream API picks the retailer per request (checkers | dischem) — each has
// its own supplier list and report types; the backend proxy maps these
// dashboard codes (CHK/DIS) to the upstream key.
const CRAWLABLE_RETAILERS = ["CHK", "DIS"];

// MediRite (MED) and Petshop (PET) are virtual banners under the Checkers
// portal — their data is pulled by the Checkers crawler, so they crawl AS
// Checkers (same supplier list, email-only delivery). Map them to CHK for the
// crawl request; everything else passes through unchanged.
const crawlRetailerFor = (r) => (r === "MED" || r === "PET" ? "CHK" : r);

const RETAILER_LABELS = {
  PNP: "Pick n Pay",
  CHK: "Checkers",
  DIS: "Dis-Chem",
  MAK: "Makro",
  CLI: "Clicks",
  MED: "MediRite",
  PET: "Petshop",
};

const REPORT_LABELS = {
  sales: "Sales / Stores",
  dc: "DC",
  sdc: "SDC (Stock by Article)",
};

// Dis-Chem delivers via FTP + email; Checkers via email only (FTP disabled).
// `retailerKey` is the upstream value echoed back on the job ("checkers"|"dischem").
const deliveryVerb = (retailerKey) =>
  retailerKey === "dischem" ? "FTP'd and emailed to" : "emailed to";

// Poll cadence + cap (handoff §5: allow ~5 min per supplier).
const POLL_MS = 15000;

const Edi = () => {
  const toast = useToast();
  const theme = useTheme();
  const { selectedRetailer } = useSupplierRetailer();
  const userid = localStorage.getItem("userid");

  const [supplierList, setSupplierList] = useState([]);
  const [reportTypes, setReportTypes] = useState(["sales", "dc"]);
  const [report, setReport] = useState("sales");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [search, setSearch] = useState("");

  const [job, setJob] = useState(null); // { jobId, status, report, suppliers }
  const [submitting, setSubmitting] = useState(false);
  const pollRef = useRef(null);

  // The supplier registry table needs more horizontal room than the crawl form.
  const [tabIndex, setTabIndex] = useState(0);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const rowHoverBg = useColorModeValue("gray.50", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");

  const retailerLabel = RETAILER_LABELS[selectedRetailer] || selectedRetailer || "None";
  const crawlRetailer = crawlRetailerFor(selectedRetailer); // physical crawl retailer (MED/PET -> CHK)
  const isCrawlable = CRAWLABLE_RETAILERS.includes(crawlRetailer);
  const isVirtualChk = isCrawlable && crawlRetailer !== selectedRetailer; // MED/PET crawling via Checkers

  // Fetch the crawlable supplier list + valid report types for the selected
  // retailer. Refetches whenever the retailer changes — CHK and DIS have
  // different supplier lists and report types. Selection + search are cleared on
  // switch so stale names from the other retailer can't be submitted.
  useEffect(() => {
    setSelectedSuppliers([]);
    setSearch("");
    // Non-crawlable retailer (PNP/CLI/MAK) — nothing to load.
    if (!CRAWLABLE_RETAILERS.includes(crawlRetailer)) {
      setSupplierList([]);
      setLoadError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const res = await axios.get(`${API}/edi-crawler/suppliers`, {
          params: { userid, retailer: crawlRetailer },
        });
        if (cancelled) return;
        const data = res.data?.data || {};
        setSupplierList(data.suppliers || []);
        if (Array.isArray(data.reports) && data.reports.length > 0) {
          setReportTypes(data.reports);
          setReport((prev) => (data.reports.includes(prev) ? prev : data.reports[0]));
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.message || e.message || "Failed to load suppliers";
          setLoadError(msg);
          toast({ status: "error", title: "Failed to load suppliers", description: msg, duration: 5000, isClosable: true });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userid, crawlRetailer, toast]);

  // Stop polling on unmount.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const filteredSuppliers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return supplierList;
    return supplierList.filter((s) => s.toLowerCase().includes(term));
  }, [supplierList, search]);

  const toggleSupplier = (name) => {
    setSelectedSuppliers((prev) =>
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    );
  };

  const allFilteredSelected =
    filteredSuppliers.length > 0 && filteredSuppliers.every((s) => selectedSuppliers.includes(s));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      const set = new Set(filteredSuppliers);
      setSelectedSuppliers((prev) => prev.filter((s) => !set.has(s)));
    } else {
      setSelectedSuppliers((prev) => Array.from(new Set([...prev, ...filteredSuppliers])));
    }
  };

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const pollJob = (jobId) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/edi-crawler/jobs/${encodeURIComponent(jobId)}`, { params: { userid } });
        const data = res.data?.data || {};
        setJob((prev) => ({ ...prev, ...data }));
        if (data.status === "done" || data.status === "failed") {
          stopPolling();
          toast({
            status: data.status === "done" ? "success" : "error",
            title: data.status === "done" ? "Crawl run finished" : "Crawl run failed",
            description:
              data.status === "done"
                ? `Reports ${deliveryVerb(data.retailer)} info@spearbi.org. Check the emails/log for per-supplier results.`
                : "The run crashed or timed out. Open the job log to inspect.",
            duration: 8000,
            isClosable: true,
          });
        }
      } catch (e) {
        // 404 after a service restart means the in-memory job is gone — stop quietly.
        if (e?.response?.status === 404) stopPolling();
      }
    }, POLL_MS);
  };

  const canCrawl = isCrawlable && selectedSuppliers.length > 0 && !submitting;

  const handleCrawl = async () => {
    if (!canCrawl) return;
    setSubmitting(true);
    setJob(null);
    try {
      const res = await axios.post(`${API}/edi-crawler/jobs`, {
        userid,
        retailer: crawlRetailer,
        report,
        suppliers: selectedSuppliers,
      });
      const data = res.data?.data || {};
      setJob(data);
      toast({
        status: "success",
        title: "Crawl queued",
        description: `Job ${data.jobId || ""} queued for ${selectedSuppliers.length} supplier(s).`,
        duration: 6000,
        isClosable: true,
      });
      if (data.jobId) pollJob(data.jobId);
    } catch (e) {
      const details = e?.response?.data?.details;
      let msg = e?.response?.data?.message || e.message || "Failed to queue crawl";
      // Surface the upstream's actionable detail when present.
      if (details?.unknown?.length) msg += ` (unknown: ${details.unknown.join(", ")})`;
      else if (details?.reports?.length) msg += ` (valid reports: ${details.reports.join(", ")})`;
      toast({ status: "error", title: "Crawl failed", description: msg, duration: 6000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  const jobStatusColor = (status) => {
    if (status === "done") return "green";
    if (status === "failed") return "red";
    if (status === "running") return "blue";
    return "yellow"; // queued
  };

  return (
    <Box mt={16} mx="auto" maxW={tabIndex === 1 ? "1200px" : "900px"} px={6}>
      <Flex align="center" justify="space-between" mb={2} wrap="wrap" gap={3}>
        <Box>
          <Heading size="lg" mb={1}>EDI Crawlers</Heading>
          <Text color={mutedColor}>Run on-demand web crawlers, and manage the supplier logins they use.</Text>
        </Box>
        <Flex align="center" gap={2}>
          <Text fontSize="sm" color={mutedColor}>Retailer:</Text>
          <Badge fontSize="sm" px={3} py={1} borderRadius="md" colorScheme={isCrawlable ? "green" : "gray"}>
            {retailerLabel}
          </Badge>
        </Flex>
      </Flex>

      {!isCrawlable && (
        <Alert status="warning" mt={4} mb={2} borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Crawling is only available for <strong>Checkers</strong> and <strong>Dis-Chem</strong>{" "}
            (and <strong>MediRite</strong>/<strong>Petshop</strong>, which crawl via Checkers).
            Select one of those retailers in the top navigation to enable crawling.
          </AlertDescription>
        </Alert>
      )}

      {isVirtualChk && (
        <Alert status="info" mt={4} mb={2} borderRadius="md">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            {retailerLabel} data is pulled by the <strong>Checkers</strong> crawler — the list below is
            the Checkers supplier list, and reports are emailed to <strong>info@spearbi.org</strong>.
          </AlertDescription>
        </Alert>
      )}

      <Tabs mt={4} index={tabIndex} onChange={setTabIndex} colorScheme="green" variant="enclosed">
        <TabList>
          <Tab fontSize="sm">Run crawl</Tab>
          <Tab fontSize="sm">Suppliers</Tab>
          <Tab fontSize="sm">Alert watch lists</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt={4}>

      <Box bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" boxShadow="sm" p={5}>
        {/* Report type */}
        <Text fontWeight="bold" fontSize="md" mb={2}>Report type</Text>
        <ButtonGroup size="sm" isAttached variant="outline" mb={2}>
          {reportTypes.map((rt) => (
            <Button
              key={rt}
              onClick={() => setReport(rt)}
              colorScheme={report === rt ? "green" : "gray"}
              variant={report === rt ? "solid" : "outline"}
            >
              {REPORT_LABELS[rt] || rt}
            </Button>
          ))}
        </ButtonGroup>
        <Text fontSize="xs" color={mutedColor} mb={4}>One report type per run — submit again to pull the other.</Text>

        <Divider mb={4} />

        {/* Suppliers */}
        <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
          <Text fontWeight="bold" fontSize="md">
            Suppliers ({selectedSuppliers.length}/{supplierList.length})
          </Text>
          <Button size="xs" variant="outline" onClick={toggleSelectAll} isDisabled={filteredSuppliers.length === 0}>
            {allFilteredSelected ? "Deselect All" : "Select All"}
          </Button>
        </Flex>

        <InputGroup size="sm" mb={4}>
          <InputLeftElement pointerEvents="none">
            <FaSearch color={theme.colors.gray?.[400] || "#A0AEC0"} />
          </InputLeftElement>
          <Input placeholder="Search suppliers..." value={search} onChange={(e) => setSearch(e.target.value)} borderRadius="md" />
        </InputGroup>

        <Box maxH="calc(100vh - 480px)" minH="220px" overflowY="auto" pr={2}>
          {isLoading ? (
            <Flex justify="center" align="center" py={10}><Spinner /></Flex>
          ) : loadError ? (
            <Flex justify="center" align="center" py={10}><Text fontSize="sm" color="red.400">{loadError}</Text></Flex>
          ) : filteredSuppliers.length === 0 ? (
            <Flex justify="center" align="center" py={10}>
              <Text fontSize="sm" color={mutedColor}>
                {supplierList.length === 0 ? "No suppliers returned by the crawler service." : "No suppliers match your search."}
              </Text>
            </Flex>
          ) : (
            filteredSuppliers.map((name) => (
              <Flex
                key={name}
                align="center"
                py={2}
                px={3}
                borderRadius="md"
                _hover={{ bg: rowHoverBg }}
                cursor="pointer"
                onClick={() => toggleSupplier(name)}
              >
                <Checkbox isChecked={selectedSuppliers.includes(name)} onChange={() => toggleSupplier(name)} mr={3} pointerEvents="none" />
                <Text fontSize="sm" fontWeight="medium">{name}</Text>
              </Flex>
            ))
          )}
        </Box>

        <Flex mt={5} justify="space-between" align="center" wrap="wrap" gap={3}>
          <Text fontSize="sm" color={mutedColor}>
            {selectedSuppliers.length > 0
              ? `${selectedSuppliers.length} supplier(s) · ${REPORT_LABELS[report] || report}`
              : "Select one or more suppliers to crawl."}
          </Text>
          <Button leftIcon={<FaSpider />} colorScheme="green" onClick={handleCrawl} isDisabled={!canCrawl} isLoading={submitting} loadingText="Queuing...">
            Crawl
          </Button>
        </Flex>
      </Box>

      {/* Job status */}
      {job && (
        <Box mt={4} bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" boxShadow="sm" p={5}>
          <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
            <Box>
              <Text fontWeight="bold" fontSize="md">Latest run</Text>
              <Text fontSize="xs" color={mutedColor}>{job.jobId}</Text>
            </Box>
            <Badge colorScheme={jobStatusColor(job.status)} px={3} py={1} borderRadius="md">
              {job.status || "queued"}
            </Badge>
          </Flex>
          <Text fontSize="sm" color={mutedColor} mt={3}>
            {job.report ? `${REPORT_LABELS[job.report] || job.report} · ` : ""}
            {(job.suppliers || []).length} supplier(s). Reports are {deliveryVerb(job.retailer)}{" "}
            <strong>info@spearbi.org</strong>.
          </Text>
          {job.status === "done" && (
            <Text fontSize="xs" color="orange.500" mt={2}>
              "Done" means the run finished — it does not guarantee every supplier succeeded. Check the emails to confirm per-supplier results.
            </Text>
          )}
        </Box>
      )}

          </TabPanel>

          {/* Supplier registry — the crawler logins for this retailer. Edits here drive
              BOTH the on-demand crawls above and the scheduled cron runs on the box. */}
          <TabPanel px={0} pt={4}>
            <SupplierRegistry crawlRetailer={crawlRetailer} retailerLabel={retailerLabel} isCrawlable={isCrawlable} />
          </TabPanel>

          {/* Stock alert watch lists. Independent of the retailer selector above —
              the alerts only exist for PNP and Checkers, and are chosen inline. */}
          <TabPanel px={0} pt={4}>
            <AlertSuppliers />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default Edi;
